import { Link, useNavigate, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import WorkCard from "@/components/feature/WorkCard";
import FavoriteButton from "@/components/feature/FavoriteButton";
import ShareButton from "@/components/feature/ShareButton";
import EmptyState from "@/components/feature/EmptyState";
import { works } from "@/mocks/works";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

export default function WorkDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const work = works.find((item) => item.id === id);

  useDocumentMeta(
    work ? `${work.title} - 吴梦梦影视官网` : "作品未找到 - 吴梦梦影视官网",
    work
      ? `《${work.title}》是吴梦梦主演的${work.type}，${work.director}执导，${work.cast.join("、")}联合出演，${work.year}年${work.status}。查看剧情简介、作品资料与相关推荐。`
      : "该作品可能已下架或链接有误，请返回作品列表浏览吴梦梦的全部影视作品。",
    work ? `${work.title},吴梦梦${work.type},${work.tags.join(",")}` : "吴梦梦影视作品",
  );

  if (!work) {
    return (
      <div className="container-content py-10 md:py-16">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "作品", to: "/works" }, { label: "未找到" }]} />
        <div className="mt-8">
          <EmptyState
            icon="ri-file-unknow-line"
            title="没有找到这部作品"
            description="链接可能已经变更，或者这部作品暂时还没有收录。回到作品列表看看其他内容吧。"
            actionLabel="返回作品列表"
            onAction={() => navigate("/works")}
          />
        </div>
      </div>
    );
  }

  const related = works
    .filter(
      (item) =>
        item.id !== work.id &&
        (item.type === work.type || item.tags.some((tag) => work.tags.includes(tag))),
    )
    .slice(0, 4);

  const metaRows = [
    { label: "类型", value: work.type },
    { label: "导演", value: work.director },
    { label: "主演", value: work.cast.join(" / ") },
    { label: "上映时间", value: work.releaseDate },
    { label: "时长", value: work.duration },
    { label: "上映状态", value: work.status },
  ];

  return (
    <div>
      <section className="relative overflow-hidden bg-primary-950">
        <img
          src={work.cover}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover object-top opacity-25 blur-[2px] scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-primary-950/96 via-primary-950/88 to-primary-900/75" />

        <div className="relative container-content py-6 md:py-10">
          <Breadcrumb
            tone="onDark"
            items={[{ label: "首页", to: "/" }, { label: "作品", to: "/works" }, { label: work.title }]}
          />

          <div className="mt-6 md:mt-8 flex flex-col md:flex-row gap-6 md:gap-10">
            <div className="relative shrink-0 w-[190px] sm:w-[220px] lg:w-[292px] mx-auto md:mx-0">
              <div className="aspect-[3/4] rounded-lg overflow-hidden border border-background-50/15 bg-primary-900">
                <img
                  src={work.cover}
                  alt={`${work.title} 海报`}
                  title={`${work.title} 吴梦梦${work.type}`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <span className="absolute -bottom-2.5 left-3 px-2.5 py-1 rounded bg-accent-500 text-primary-950 text-[11px] font-semibold">
                {work.status}
              </span>
            </div>

            <div className="min-w-0 flex-1 md:pt-1">
              <div className="flex flex-wrap items-center gap-2 text-[11px] text-background-300">
                <span className="px-2 py-0.5 rounded border border-background-50/25">{work.type}</span>
                <span>{work.year} 年</span>
                <span className="text-background-400">·</span>
                <span>{work.duration}</span>
                <span className="text-background-400">·</span>
                <span>{work.tags.join(" / ")}</span>
              </div>

              <h1 className="mt-3 text-3xl sm:text-4xl md:text-5xl font-black text-background-50 tracking-tight">
                {work.title}
              </h1>

              <div className="mt-4 flex items-center gap-3 flex-wrap">
                <div className="flex items-baseline gap-1.5">
                  <span className="font-label text-3xl font-extrabold text-accent-400">
                    {work.rating.toFixed(1)}
                  </span>
                  <span className="text-xs text-background-300">/ 10 演示评分</span>
                </div>
                <span className="w-px h-6 bg-background-50/20 hidden sm:block" />
                <div className="flex items-center gap-1 text-accent-400">
                  {[1, 2, 3, 4, 5].map((index) => (
                    <i
                      key={index}
                      className={`${work.rating >= index * 1.7 ? "ri-star-fill" : "ri-star-line"} text-xs leading-none`}
                    />
                  ))}
                </div>
              </div>

              <p className="mt-4 max-w-2xl text-sm text-background-200/85 leading-relaxed line-clamp-3">
                {work.synopsis}
              </p>

              <div className="mt-6 flex flex-wrap items-center gap-2.5">
                <FavoriteButton
                  variant="pill"
                  item={{
                    key: `work:${work.id}`,
                    type: "work",
                    id: work.id,
                    title: work.title,
                    cover: work.cover,
                    meta: `${work.type} · ${work.year}`,
                  }}
                />
                <ShareButton
                  title={`${work.title} - 吴梦梦影视官网`}
                  summary={work.synopsis}
                  tone="onDark"
                />
                <Link
                  to="/works"
                  className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md text-sm text-background-200 hover:text-background-50 hover:bg-background-50/10 transition-colors whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line text-base leading-none" />
                  返回作品列表
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="container-content py-9 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-9 lg:gap-12">
          <div>
            <h2 className="text-lg md:text-xl font-bold flex items-center gap-2">
              <span className="w-1 h-4 rounded-full bg-accent-500" />
              剧情简介
            </h2>
            <p className="mt-4 text-[15px] leading-[1.95] text-foreground-700">{work.synopsis}</p>

            <p className="mt-4 text-[15px] leading-[1.95] text-foreground-700">
              本页作品资料为演示示例内容，评分与热度数据仅用于界面展示，不代表真实票房或口碑统计。影片的正式上线信息请以官方发布为准。
            </p>

            <h2 className="mt-9 text-lg md:text-xl font-bold flex items-center gap-2">
              <span className="w-1 h-4 rounded-full bg-accent-500" />
              主演阵容
            </h2>
            <div className="mt-4 flex flex-wrap gap-2">
              {work.cast.map((person) => (
                <span
                  key={person}
                  className="inline-flex items-center gap-2 h-9 pl-1.5 pr-3.5 rounded-full border border-background-300 bg-background-50 text-sm text-foreground-800"
                >
                  <span className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-100 text-primary-700">
                    <i className="ri-user-line text-[11px] leading-none" />
                  </span>
                  {person}
                </span>
              ))}
            </div>

            <h2 className="mt-9 text-lg md:text-xl font-bold flex items-center gap-2">
              <span className="w-1 h-4 rounded-full bg-accent-500" />
              创作标签
            </h2>
            <div className="mt-4 flex flex-wrap gap-2">
              {[...work.tags, work.type, `${work.year}`].map((tag) => (
                <span key={tag} className="px-2.5 h-7 inline-flex items-center rounded-md bg-secondary-100 text-secondary-900 text-xs">
                  {tag}
                </span>
              ))}
            </div>
          </div>

          <aside className="flex flex-col gap-5">
            <div className="rounded-lg border border-background-200 bg-background-50 overflow-hidden">
              <div className="px-4 py-3.5 border-b border-background-200">
                <h2 className="text-sm font-semibold">作品资料</h2>
              </div>
              <dl className="divide-y divide-background-200">
                {metaRows.map((row) => (
                  <div key={row.label} className="flex gap-3 px-4 py-3">
                    <dt className="shrink-0 w-16 text-xs text-foreground-500">{row.label}</dt>
                    <dd className="flex-1 text-xs text-foreground-900 leading-relaxed">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <div className="rounded-lg border border-accent-200 bg-accent-50 px-4 py-4">
              <div className="flex items-center gap-2 text-accent-800">
                <i className="ri-bookmark-3-line text-base leading-none" />
                <span className="text-sm font-semibold">收藏这部作品</span>
              </div>
              <p className="mt-2 text-xs text-accent-900/80 leading-relaxed">
                收藏会保存在本机浏览器中，刷新页面后仍然保留，最多可保存 50 条作品与资讯。
              </p>
            </div>
          </aside>
        </div>
      </section>

      {related.length > 0 && (
        <section className="bg-background-100/70 border-t border-background-200">
          <div className="container-content py-10 md:py-14">
            <div className="flex items-end justify-between gap-4 mb-6">
              <div>
                <span className="text-[10px] md:text-[11px] font-label tracking-[0.22em] text-accent-600 uppercase">
                  Related Works
                </span>
                <h2 className="mt-2 text-xl md:text-[26px] font-bold">相关作品推荐</h2>
              </div>
              <Link
                to="/works"
                className="shrink-0 inline-flex items-center gap-1 text-xs md:text-sm text-foreground-600 hover:text-accent-600 transition-colors whitespace-nowrap"
              >
                全部作品
                <i className="ri-arrow-right-line text-sm leading-none" />
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5">
              {related.map((item) => (
                <WorkCard key={item.id} work={item} />
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}